/* eslint-disable no-console */
import { Helmet } from "react-helmet-async";
import { Route, Routes, Navigate } from "react-router-dom";
import { Loader, PrivateRoute, FichaEdificioInventario, SoloEscritorio } from "./components";
import {
  ADMIN_IDS,
  ER_ROLES,
  ER_V2_IDS,
  INVERSORES_RESTRINGIDOS,
  puedeVerERv2,
  REGION_IDS,
} from "./constants/permisos";
import { ROLES } from "./constants/roles";
import { useAuthContext } from "./context/auth";
import AuthCallback from "./context/AuthCallback";
import {
  Admin,
  Api,
  CalculadorComisionEdificio,
  CargarCliente,
  CargarFactura,
  CargarIntermediario,
  CargarOportunidad,
  CarteleriaPage,
  Categoria,
  CategoriaAutomotriz,
  CategoriaBebidas,
  CategoriaBelleza,
  CategoriaDelivery,
  CategoriaFastFood,
  CategoriaSmartphones,
  CategoriaInversiones,
  CategoriaMascotas,
  CategoriaPerfumes,
  CategoriaRetail,
  CategoriaTelcos,
  CategoriaTransporte,
  CategoriaVacaciones,
  CategoriaViajes,
  CategoriaVestimenta,
  ClientesCRM,
  ClientesCartera,
  ConciliacionSIIPage,
  CuentasBancariasPage,
  DashboardFinancieroPage,
  DashboardIvaPage,
  EstadoCuentaPage,
  FactoringPage,
  CreditosPage,
  CotizadorProgrammatic,
  CotizadorProgrammaticEditar,
  ER,
  ERv2,
  Edificios,
  FacturasEjecutivo,
  FlujoCajaV2Page,
  MovimientosBancariosPage,
  MisCotizacionesPage,
  GaleriaPage,
  ReporteFotograficoPage,
  ReporteFotograficoExternoPage,
  GestionActivosPage,
  GestionEmpleados,
  GestionProveedoresPage,
  MailingPage,
  Home,
  InventarioNuevo,
  InfraInventarioPantallasPage,
  InfraRondaMobilePage,
  InvestigacionIpsos,
  SimuladorImpacto,
  Landing,
  LandingTD,
  ListadoB2Bnuevo,
  ListadoCotizaciones,
  ListadoVetos,
  Login,
  Mapa,
  Mediakit,
  MediakitV2,
  MediakitEdificios,
  MediakitEdificiosFullCbre,
  NotFound,
  Oportunidad,
  OportunidadFactura,
  Perfil,
  PortafolioFichaEdificios,
  Redirect,
  TransformacionDigital,
  TransformacionDigitalVentas,
  Usuarios,
  VentasBTLpage,
  VentasEjecutivo,
  VentasFactura,
  VentasProyeccion,
  VentasRegionReciente,
} from "./pages";
import BackfillVideos from "./pages/BackfillVideos";
import MediakitEdificiosFull from "./pages/MediakitEdificiosFull";
import Ventas from "./pages/Ventas";

const App = () => {
  const { session, pais, isLoading } = useAuthContext();

  const getMetaData = () => {
    if (pais === "Perú") {
      return {
        title: "Conectamos Marcas con Audiencias Premium",
        description: "Conectamos marcas y audiencias de manera eficiente.",
        canonical: "https://visiona.pe",
        hreflang: [
          { hreflang: "es-PE", href: "https://visiona.pe" },
          { hreflang: "es-CL", href: "https://wecast.cl" },
        ],
      };
    } else if (pais === "Chile") {
      return {
        title: "Conectamos Marcas con Audiencias Premium",
        description: "Conectamos marcas y audiencias de manera eficiente.",
        canonical: "https://wecast.cl",
        hreflang: [
          { hreflang: "es-PE", href: "https://visiona.pe" },
          { hreflang: "es-CL", href: "https://wecast.cl" },
        ],
      };
    } else {
      // Por defecto (si el país no está definido)
      return {
        title: "Conectamos Marcas con Audiencias Premium",
        description: "Conectamos marcas y audiencias de manera eficiente.",
        canonical: "https://visiona.pe",
        hreflang: [
          { hreflang: "es-PE", href: "https://visiona.pe" },
          { hreflang: "es-CL", href: "https://wecast.cl" },
        ],
      };
    }
  };

  // VideoIntro dado de baja (2026-09-12): todos los países devuelven NotFound.
  const getIntroComponent = () => <NotFound />;

  const { title, description, canonical, hreflang } = getMetaData();

  if (isLoading || session === undefined) return <Loader fullPage />;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link rel="icon" type="image/svg+xml" href={pais === "Chile" ? "/favicon-w.svg" : "/favicon-v.svg"} />
        {hreflang.map((link) => (
          <link key={link.hreflang} rel="alternate" hreflang={link.hreflang} href={link.href} />
        ))}
      </Helmet>
      <SoloEscritorio>
        <Routes>
          <Route index element={<Home />} />
          <Route path="/auth/confirm" element={<AuthCallback />} />
          <Route path="/bitcoinapi" element={<Api />} />
          <Route path="/categorias/automotriz" element={<CategoriaAutomotriz />} />
          <Route path="/categorias/bebidas" element={<CategoriaBebidas />} />
          <Route path="/categorias/belleza" element={<CategoriaBelleza />} />
          <Route path="/categorias/fastfood" element={<CategoriaFastFood />} />
          <Route path="/categorias/delivery" element={<CategoriaDelivery />} />
          <Route path="/categorias/smartphones" element={<CategoriaSmartphones />} />
          <Route path="/categorias/inversiones" element={<CategoriaInversiones />} />
          <Route path="/categorias/mascotas" element={<CategoriaMascotas />} />
          <Route path="/categorias/perfumes" element={<CategoriaPerfumes />} />
          <Route path="/categorias/retail" element={<CategoriaRetail />} />
          <Route path="/categorias/telcos" element={<CategoriaTelcos />} />
          <Route path="/categorias/transporte" element={<CategoriaTransporte />} />
          <Route path="/categorias/vacaciones" element={<CategoriaVacaciones />} />
          <Route path="/categorias/viajes" element={<CategoriaViajes />} />
          <Route path="/categorias/vestimenta" element={<CategoriaVestimenta />} />
          <Route path="/edificios" element={<MediakitEdificios />} />
          <Route path="/ficha-edificio/:id" element={<FichaEdificioInventario />} />
          <Route path="/galeria" element={<GaleriaPage />} />
          <Route path="/intro" element={getIntroComponent()} />
          <Route path="/inventario" element={<InventarioNuevo />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/listadoempresas" element={<ListadoB2Bnuevo />} />
          <Route path="/listadovetos" element={<ListadoVetos />} />
          <Route path="/mapa" element={<Mapa />} />
          <Route path="/mediakit" element={<Mediakit />} />
          <Route path="/mediakit2" element={<MediakitV2 />} />
          <Route path="/mediakit-edificios-full" element={<MediakitEdificiosFull />} />
          <Route path="/mediakit-edificios-full-cbre" element={<MediakitEdificiosFullCbre />} />
          <Route path="/investigacion-ipsos" element={<InvestigacionIpsos />} />
          <Route path="/simulador-impacto" element={<SimuladorImpacto />} />
          <Route path="/portafolio" element={<PortafolioFichaEdificios />} />
          <Route path="/redirect" element={<Redirect />} />
          <Route path="/td" element={<LandingTD />} />
          <Route path="/transformacion-digital" element={<TransformacionDigital />} />
          <Route path="/transformacion-digital-ventas" element={<TransformacionDigitalVentas />} />
          {/* /carteleriiaa retirada del bloque público 2026-09-26 (decisión de Ignacio, tanda A5 de
              seguridad) — la herramienta de cartelería no se usa sin login. Redirect para que un
              marcador viejo caiga en el login (vía /admin/carteleriiaa) y no en un 404. */}
          <Route path="/carteleriiaa" element={<Navigate to="/admin/carteleriiaa" replace />} />

          {session !== null ? (
            <>
              <Route
                path="/admin"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.ADMINISTRATIVO,
                      ROLES.AGENCIA,
                      ROLES.BOARD,
                      ROLES.BROKER,
                      ROLES.COMERCIAL,
                      ROLES.CONTABLE,
                      ROLES.EDIFICIO,
                      ROLES.GERENCIA,
                      ROLES.INFRAESTRUCTURA,
                      ROLES.INVERSOR,
                      ROLES.SUPERADMIN,
                      ROLES.CLIENTE,
                    ]}
                  >
                    <Admin />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/er"
                element={
                  <PrivateRoute allowedRoles={ER_ROLES} deniedIds={INVERSORES_RESTRINGIDOS}>
                    <ER />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/er-v2"
                element={
                  <PrivateRoute
                    // Gate primario (Perú y resto): rol financiero ∧ id ∈ ER_V2_IDS — igual que antes.
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.ADMINISTRATIVO]}
                    allowedIds={ER_V2_IDS}
                    // Parte aditiva: fuente única compartida con el gate interno de ERv2/index.js.
                    alsoAllow={puedeVerERv2}
                  >
                    <ERv2 />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/cargar-cliente"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD, ROLES.INVERSOR]}
                    deniedIds={INVERSORES_RESTRINGIDOS}
                  >
                    <CargarCliente />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/cargar-factura"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL]}>
                    <CargarFactura />
                  </PrivateRoute>
                }
              />
              <Route
                path="/agencias"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA]}>
                    <CargarIntermediario />
                  </PrivateRoute>
                }
              />
              {/* Redirect de la ruta vieja para no romper accesos guardados. */}
              <Route path="/admin/cargar-intermediario" element={<Navigate to="/agencias" replace />} />
              <Route
                path="/admin/cargar-oportunidad"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL]}>
                    <CargarOportunidad />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/factura/:oportunidadId"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.COMERCIAL, ROLES.ADMINISTRATIVO]}>
                    <OportunidadFactura />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/oportunidades/editar/:oportunidadId"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BROKER,
                    ]}
                  >
                    <Oportunidad />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/informes-comision"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.ADMINISTRATIVO,
                      ROLES.COMERCIAL,
                      ROLES.CONTABLE,
                      ROLES.EDIFICIO,
                      ROLES.BOARD,
                      ROLES.INVERSOR,
                    ]}
                    deniedIds={INVERSORES_RESTRINGIDOS}
                  >
                    <CalculadorComisionEdificio />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/online-cotizador"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.AGENCIA,
                      ROLES.CLIENTE,
                      ROLES.BOARD,
                      ROLES.INVERSOR,
                      ROLES.BROKER,
                    ]}
                  >
                    <CotizadorProgrammatic />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/mis-cotizaciones"
                element={
                  <PrivateRoute allowedRoles={[ROLES.AGENCIA, ROLES.CLIENTE]}>
                    <MisCotizacionesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/cotizaciones-generadas"
                element={<Navigate to="/admin/ventas/cotizaciones" replace />}
              />
              <Route
                path="/admin/reporte-fotografico"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                      ROLES.INVERSOR,
                      ROLES.BROKER,
                    ]}
                  >
                    <ReporteFotograficoPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/reporte-fotografico-externo"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.AGENCIA,
                      ROLES.CLIENTE,
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.BROKER,
                    ]}
                  >
                    <ReporteFotograficoExternoPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/backfill-videos"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA]}>
                    <BackfillVideos />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/carteleriiaa"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                    ]}
                  >
                    <CarteleriaPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/online-cotizador/editar/:id"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BROKER]}>
                    <CotizadorProgrammaticEditar />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/ventas-BTL"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL]}>
                    <VentasBTLpage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/perfil"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.COMERCIAL, ROLES.GERENCIA]}>
                    <Perfil />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/usuarios"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                      ROLES.INVERSOR,
                      ROLES.INFRAESTRUCTURA,
                    ]}
                    deniedIds={INVERSORES_RESTRINGIDOS}
                  >
                    <Usuarios />
                  </PrivateRoute>
                }
              />
              {/* Hub de ventas: una sola puerta (7 roles) para las 4 visualizaciones, sin gate por
                tab. deniedIds={INVERSORES_RESTRINGIDOS} por consistencia (hoy array vacío = no-op).
                El hub rutea internamente /admin/ventas/{detalle,crm,mapa-calor,cotizaciones}. */}
              <Route
                path="/admin/ventas/*"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                      ROLES.INVERSOR,
                      ROLES.BROKER,
                    ]}
                    deniedIds={INVERSORES_RESTRINGIDOS}
                  >
                    <Ventas />
                  </PrivateRoute>
                }
              />
              {/* Redirects de las 4 rutas viejas al hub. "/admin/ventas" (base) y cualquier
                "/admin/ventas/*" desconocido los resuelve el propio hub → /admin/ventas/detalle. */}
              <Route path="/admin/ventas-detalle" element={<Navigate to="/admin/ventas/detalle" replace />} />
              <Route path="/admin/ventas-facturas" element={<Navigate to="/admin/facturacion" replace />} />
              <Route
                path="/admin/facturacion"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                      ROLES.CONTABLE,
                      ROLES.INVERSOR,
                    ]}
                  >
                    <VentasFactura />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/ventas-proyeccion"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                      ROLES.INVERSOR,
                      ROLES.CONTABLE,
                      ROLES.BROKER,
                    ]}
                    deniedIds={INVERSORES_RESTRINGIDOS}
                  >
                    <VentasProyeccion />
                  </PrivateRoute>
                }
              />
              <Route path="/admin/mis-ventas" element={<Navigate to="/admin/ventas/crm" replace />} />
              <Route
                path="/admin/mis-ventas-baca"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD]}>
                    <VentasEjecutivo />
                  </PrivateRoute>
                }
              />
              <Route path="/admin/mapa-calor" element={<Navigate to="/admin/ventas/mapa-calor" replace />} />
              <Route
                path="/admin/gestion-activos"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                  >
                    <GestionActivosPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/flujo-caja"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                  >
                    <FlujoCajaV2Page />
                  </PrivateRoute>
                }
              />
              <Route path="/admin/flujo-caja-old" element={<Navigate to="/admin/flujo-caja" replace />} />
              <Route
                path="/admin/dashboard-financiero"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                  >
                    <DashboardFinancieroPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/dashboard-iva"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                  >
                    <DashboardIvaPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/estado-cuenta"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                  >
                    <EstadoCuentaPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/factoring"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                  >
                    <FactoringPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/creditos"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}
                  >
                    <CreditosPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/conciliacion-sii"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}>
                    <ConciliacionSIIPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/movimientos-bancarios"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}>
                    <MovimientosBancariosPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/cuentas-bancarias"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.ADMINISTRATIVO]}>
                    <CuentasBancariasPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/mis-facturas"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                      ROLES.INVERSOR,
                      ROLES.CONTABLE,
                    ]}
                    deniedIds={INVERSORES_RESTRINGIDOS}
                  >
                    <FacturasEjecutivo />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/ventas-region"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD]}
                    allowedIds={REGION_IDS}
                  >
                    <VentasRegionReciente />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/edificios"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                      ROLES.INFRAESTRUCTURA,
                      ROLES.COMERCIAL,
                      ROLES.CONTABLE,
                      ROLES.INVERSOR,
                    ]}
                  >
                    <Edificios />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/infraestructura/pantallas"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.INFRAESTRUCTURA,
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                    ]}
                  >
                    <InfraInventarioPantallasPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/infraestructura/ronda"
                element={
                  <PrivateRoute allowedRoles={[ROLES.INFRAESTRUCTURA, ROLES.SUPERADMIN, ROLES.GERENCIA]}>
                    <InfraRondaMobilePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/categorias"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.AGENCIA,
                      ROLES.CLIENTE,
                      ROLES.BOARD,
                      ROLES.EDIFICIO,
                      ROLES.BROKER,
                    ]}
                  >
                    <Categoria />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/cotizaciones"
                element={
                  <PrivateRoute
                    allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.COMERCIAL, ROLES.BOARD, ROLES.BROKER]}
                  >
                    <ListadoCotizaciones />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/clientes"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.BOARD]}>
                    <ClientesCRM />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/clientes-cartera"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                      ROLES.BOARD,
                      ROLES.BROKER,
                    ]}
                  >
                    <ClientesCartera />
                  </PrivateRoute>
                }
              />
              <Route path="/login" element={<Navigate to="/admin" replace />} />
              <Route
                path="/admin/mailing"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      ROLES.SUPERADMIN,
                      ROLES.GERENCIA,
                      ROLES.BOARD,
                      ROLES.COMERCIAL,
                      ROLES.ADMINISTRATIVO,
                    ]}
                    allowedIds={ADMIN_IDS}
                  >
                    <MailingPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/gestion-empleados"
                element={
                  <PrivateRoute allowedRoles={[ROLES.GERENCIA, ROLES.ADMINISTRATIVO]}>
                    <GestionEmpleados />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/proveedores"
                element={
                  <PrivateRoute allowedRoles={[ROLES.SUPERADMIN, ROLES.GERENCIA, ROLES.ADMINISTRATIVO, ROLES.CONTABLE]}>
                    <GestionProveedoresPage />
                  </PrivateRoute>
                }
              />
            </>
          ) : (
            <>
              <Route path="*" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login />} />
            </>
          )}

          <Route path="*" element={<NotFound />} />
        </Routes>
      </SoloEscritorio>
    </>
  );
};

export default App;
